// import libs
import debounce from 'lodash/debounce'
import { select, selectAll } from 'd3-selection'
import { queryString, getHashParam, sendMessage, DOMReady, isLocal } from './utils'
import './polyfills/Fetch';

// import chart frame
import ChartFrame from './chart-frame'

// import base styles'
import './app.scss'

// import json
import openingNotes from './opening-notes.json'
import issueNotes from './issue-notes.json'
import recovNotes from './recov-notes.json'

// fix for IE
if ( typeof SVGElement.prototype.focus === 'undefined' ) {
    SVGElement.prototype.focus = function(){}
}

// enable hot module replacement
if ( module.hot ) {
	module.hot.accept()
}

// request params
const params = {
	action: 'sheet-data',
	list_id: '1y1-lhXkc_GGUTpIILukc6GmAp7-kfl2sNfOcBQcbcFY', // prod
	// list_id: '1s57UZITnLoqvnKX1Umh2QJveBopogclTUrwk4HQUaC0', // testing
}

// config constants
const provList = ['bc', 'ab', 'sk', 'mb', 'on', 'qc', 'nb', 'ns', 'pei', 'nl', 'yt', 'nwt', 'nu', 'rc', 'ca']
const dataTypes = { cases: 'confirmed cases', deaths: 'deaths', recov: 'recoveries', hosp: 'hospitalizations', icu: 'patients in ICU' }
const chartTypes = [ 'cumulative', 'histogram' ]
const host_modifier = isLocal() ? 'local.' : ''
const logScale = false

// config initials
let maxValues = []
let prevValues = []
let updateValues = []
let testsValue
let provinceName = ''
let latest = ''
let provinceUpdated = false

// config user settings
let province = queryString( 'prov' )
if ( ! province || ! provList.indexOf( province ) <= -1 ) {
	province = 'ca'
}

let dataType = queryString( 'data' ) ? queryString( 'data' ).split( '|' ) : ['cases']

let chartType = queryString( 'chart' )
if ( ! chartType || ! chartTypes.indexOf( chartType ) <= -1 ) {
	chartType = 'histogram'
}

const showTests = 'cases' === dataType[0]
const untitled = queryString( 'untitled' )
const rowOffset = 'hosp' === dataType[0] ? 41 : 2

DOMReady( () => {
	const $interactive = document.querySelector( '.l-interactive' )

	if ( untitled ) {
		$interactive.classList.add( 'l-interactive--untitled' )
	}

	if ( 'histogram' === chartType ) {
		const $navItems = $interactive.querySelectorAll( '.l-interactive__navItem' );
		[].forEach.call( $navItems, ( $elem ) => {
			$elem.classList.add( 'l-interactive__navItem--inactive' )
		})

		const $currentNav = $interactive.querySelector( `.l-interactive__navItem[data-chart-type="${chartType}"]` )
		if ( $currentNav ) {
			$currentNav.classList.remove( 'l-interactive__navItem--inactive' )
		}
	}

	// Populate with data
	fetch( 'https://globalnews.ca/gnca-ajax-redesign/covid-data/' )
		.then( response => response.text() )
		.then( ( content ) => {
			const data = JSON.parse( content )

			if ( data.feed && data.feed.entry && data.feed.entry.length > 2 ) {
				let dailyStats = []

				// Grab province label
				if ( data.feed.entry[0][`gsx$${province}-cases`] ) {
					provinceName = data.feed.entry[0][`gsx$${province}-cases`].$t
				}

				// Get today's update status
				if ( data.feed.entry[0][`gsx$${province}-deaths`] ) {
					provinceUpdated = 'UPDATED' === data.feed.entry[0][`gsx$${province}-deaths`].$t ? true : false
				}

				// Parse data for specified province
				data.feed.entry.slice( rowOffset ).forEach( ( row ) => {
					let rowData = []

					Object.keys( row ).forEach( ( key ) => {
						if ( key.indexOf( 'gsx$date' ) > -1
							|| key.indexOf( `gsx$${province}-` ) > -1 ) {
							let newKey = key.replace('gsx$', '')
							newKey = newKey.replace(`${province}-`, '')
							rowData[ newKey ] = row[ key ].$t
							rowData[ newKey ] = rowData[ newKey ].replace(/,/g, '')
							rowData[ newKey ] = '' !== rowData[ newKey ] ? rowData[ newKey ] : '0' 
						}
					})

					dailyStats.push( rowData )
				})

				// Drop last row if it contains no new data or if it's national data
				latest = 'today'
				if ( ! provinceUpdated && ( dailyStats[ dailyStats.length - 1 ][ dataType[0] ] === dailyStats[ dailyStats.length - 2 ][ dataType[0] ] )
					|| 'ca' === province ) {
					dailyStats.pop()
					latest = 'yesterday'
				}

				// calculate streak
				if ( 'today' === latest ) {
					const yesterday = dailyStats[ dailyStats.length - 2 ][ dataType[0] ]
					let streak      = 1
					let i           = 3
					
					while ( i <= dailyStats.length && yesterday === dailyStats[ dailyStats.length - i ][ dataType[0] ] ) {
						streak++
						i++
					}

					if ( streak > 1 ) {
						latest = `over past ${streak} days`
					}
				}

				// Remove rows with NaN values
				let objectsRemoved = false
				while( isNaN( dailyStats[ dailyStats.length - 1][ dataType[0] ] ) ) {
					dailyStats.pop()
					objectsRemoved = true
				}

				if ( objectsRemoved ) {
					addRemovalNote( $interactive, dailyStats[ dailyStats.length - 1 ]['date'] )
				}

				// Get max and update values
				dataType.forEach( ( type ) => {
					maxValues[ type ] = parseInt( dailyStats[ dailyStats.length - 1 ][ type ], 10 )
					prevValues[ type ] = parseInt( dailyStats[ dailyStats.length - 2 ][ type ], 10 )
					updateValues[ type ] = maxValues[ type ] - prevValues[ type ]
				})

				// Get max tests value
				testsValue = parseInt( dailyStats[ dailyStats.length - 1 ]['tests'], 10 )

				// Note any assumptions using the Esri data
				addAssumptionsNote( $interactive, dataType )

				// Note any issues associated with the data
				addIssueNotes( $interactive, province )

				// Set chart title
				setTitle({
					provinceName,
					dataType,
					chartType,
					maxValues,
					updateValues,
					latest
				})

				// Set summary stats
				if ( showTests && 'Canada' !== provinceName ) {
					setSummary({
						entries: data.feed.entry,
						provinceName
					})
				}

				// Get min y-axis bound
				let minY = 0
				if ( data.feed.entry[3] && data.feed.entry[3]['gsx$bounds'] ) {
					minY = data.feed.entry[3]['gsx$bounds'].$t
					minY = parseInt( minY, 10 )
				}

				// Initialize chart frame
				ChartFrame.init({
					data: dailyStats, 
					chartType: chartType,
					dataType: dataType,
					dataTypes: dataTypes,
					yAxisLimit: minY,
					showTests: showTests,
					logScale: logScale,
					highlights: []
				})
				
				// Populate Last Updated
				if ( data.feed.entry[2] && data.feed.entry[2]['gsx$lastupdated'] ) {
					const updated = data.feed.entry[2]['gsx$lastupdated'].$t
					const $timeStamp = document.querySelector( '.l-interactive__updated time' )
					$timeStamp.innerText = `${updated}`
				}

				// Add chart type listener
				const $nav = document.querySelectorAll( '.l-interactive__nav' );
				[].forEach.call( $nav, ( $navItem ) => {
					$navItem.addEventListener( 'click', toggleChart )
				})
			}

			// Resize after content loaded
			sendMessage({
				'height': document.documentElement.offsetHeight,
				'action': 'setHeight'
			})
		})

	// Initial sizing
	sendMessage({
		'height': document.documentElement.offsetHeight,
		'action': 'setHeight'
	})

	// bind event to capture resize and orientation change
	const resizeHandler = debounce( () => {
		ChartFrame.render()
		sendMessage({
			'height': document.documentElement.offsetHeight,
			'action': 'setHeight'
		})
	}, 200 )
	window.addEventListener( 'orientationchange', resizeHandler )
	window.addEventListener( 'resize', resizeHandler )
})

function setTitle( config ) {
	// Set province Name
	document.querySelector( '.l-interactive__locale' ).innerText = config.provinceName

	// Set multi-type flag
	if ( config.dataType.length > 1 ) {
		document.querySelector( '.l-interactive' ).classList.add( `l-interactive--multi` )
	}

	// Clear title area
	document.querySelector( '.l-interactive__title' ).innerHTML = ''

	config.dataType.forEach( ( type ) => {
		// Set interactive type
		document.querySelector( '.l-interactive' ).classList.add( `l-interactive--${type}`)

		if ( 'cumulative' === config.chartType ) {
			// Cumulative chart title
			let template = `
				<div class="l-interactive__titleRow l-interactive__titleRow--${type}">
				<span class="l-interactive__swatch"></span>
				<span class="l-interactive__figure">${config.maxValues[type].toLocaleString()}</span>
				<span class="l-interactive__class"> total cumulative </span>
				<span class="l-interactive__figure">${dataTypes[type]}</span></span>`

			document.querySelector( '.l-interactive__title' ).innerHTML += template
		} else {
			let template = ''

			// Histogram title
			if ( 'hosp' === type || 'icu' === type ) {
				template = `
					<span class="l-interactive__swatch"></span>
					<span class="l-interactive__figure">Current ${dataTypes[type]}</span>`

				if ( ! isNaN( config.maxValues[type] ) ) {
					const preamble = config.latest.indexOf <= -1 ? 'as of ' : ''

					template += `<span class="l-interactive__update"> ${config.maxValues[type].toLocaleString()} ${preamble}${config.latest}</span>`
				}
			} else {
				template = `
					<span class="l-interactive__swatch"></span>
					<span class="l-interactive__figure">New ${dataTypes[type]}</span>
					<span class="l-interactive__frequency"> by day</span>`

				if ( ! isNaN( config.updateValues[type] ) && 'yesterday' !== config.latest ) {
					template += `<span class="l-interactive__update"> +${config.updateValues[type].toLocaleString()} ${config.latest}</span>`
				}
			}

			template = `<div class="l-interactive__titleRow l-interactive__titleRow--${type}">${template}</div>`
			document.querySelector( '.l-interactive__title' ).innerHTML += template
		}
	})
}

function setSummary( config ) {
	// parse population values
	let provincePop = false
	let canadaPop = false
	const firstRow = config.entries[0]
	Object.keys( firstRow ).forEach( ( key ) => {
		if ( key.indexOf( `gsx$${province}-recov` ) > -1 ) {
			provincePop = parseInt( firstRow[ key ].$t, 10 )
		}
	})

	// parse case totals
	let provinceCases = false
	const lastRow = config.entries[ config.entries.length - 1 ]
	Object.keys( lastRow ).forEach( ( key ) => {
		if ( key.indexOf( `gsx$${province}-cases` ) > -1 ) {
			provinceCases = parseInt( lastRow[ key ].$t, 10 )
		}
	})

	// bail if bad data
	if ( isNaN( provincePop )
		|| isNaN( provinceCases ) ) {
		return;
	}

	// calculate ratios
	const provCasePerCent = ( provinceCases * 100 ) / provincePop

	// add template
	const template = `
		<span>To date, approximately 
		<span class="l-interactive__figure">${provCasePerCent.toFixed(1).toLocaleString()}% of ${config.provinceName} residents</span>
		<span> have had a confirmed case of COVID-19.</span>`

	document.querySelector( '.l-interactive__summary' ).innerHTML = template
}

function toggleChart( $navItem ) {
	if ( $navItem.target.dataset.chartType ) {
		chartType = $navItem.target.dataset.chartType
		ChartFrame.updateChartType( chartType )
		ChartFrame.render()
	}

	[].forEach.call( $navItem.target.parentElement.children, ( $elem ) => {
		$elem.classList.add( 'l-interactive__navItem--inactive' )
	})

	$navItem.target.classList.remove( 'l-interactive__navItem--inactive' )

	setTitle({
		provinceName,
		dataType,
		chartType,
		maxValues,
		updateValues
	})
}

function getReopenDate( entries, province ) {
	let reopening = false
	const firstRow = entries[0]

	Object.keys( firstRow ).forEach( ( key ) => {
		if ( key.indexOf( `gsx$${province}-tests` ) > -1 && firstRow[ key ].$t.length > 0 ) {
			let newDate = new Date( firstRow[ key ].$t )
			if ( newDate instanceof Date && ! isNaN( newDate ) ) {
				reopening = newDate
			}
		}
	})

	return reopening
}

function addReopeningNote( $interactive, province ) {
	if ( openingNotes && openingNotes[ province ] ) {
		const $footnote = $interactive.querySelector( '.l-interactive__footnote' )

		if ( $footnote ) {
			$footnote.innerText = `* ${openingNotes[ province ]}`
			$footnote.setAttribute( 'style', 'margin-top: 12px;' )
		}
	}
}

function addIssueNotes( $interactive, province ) {
	if ( issueNotes && issueNotes[ province ] ) {
		const $issuenote = $interactive.querySelector( '.l-interactive__issues' )

		if ( $issuenote ) {
			$issuenote.innerText = `${issueNotes[ province ]}`
			$issuenote.setAttribute( 'style', 'margin-top: 12px;' )
		}
	}
}

function addAssumptionsNote( $interactive, dataType ) {
	const $assumptions = $interactive.querySelector( '.l-interactive__footnote' )

	if ( 'cases' === dataType[0] ) {
		$assumptions.innerText = `Official case data may undercount the true number of cases due to limitations on the availability of tests.`;
		$assumptions.setAttribute( 'style', 'margin-top: 12px;' )
	}
}

function addRemovalNote( $interactive, lastUpdated ) {
	const $notice = $interactive.querySelector( '.l-interactive__missingData' )
	const date = new Date( lastUpdated )
	const suffix = getOrdinalSuffix( date.getDate() )
	const dateFormatted = new Intl.DateTimeFormat('en', {weekday: 'long', 'month': 'long', 'day': 'numeric'}).format( date )

	$notice.innerHTML = `This province hasn't updated their stats since <strong>${dateFormatted}${suffix}</strong>`
	$interactive.classList.add( 'l-interactive--missingData' )
}

function getOrdinalSuffix( day ) {
	const j = day % 10
	const k = day % 100
	
	if ( j == 1 && k != 11 ) {
		return "st";
	}
	
	if ( j == 2 && k != 12 ) {
		return "nd";
	}
	
	if ( j == 3 && k != 13 ) {
		return "rd";
	}
	
	return "th";
}
